import type { Expression, Map } from "mapbox-gl"

export const ZONING_SOURCE_NAME = "zoning-source"
export const ZONING_SOURCE_LAYER = "gold.zoning_source"
export const ZONING_COLOR = "#4a6fa5"
const ZONING_OUTLINE_COLOR = "#2e4b78"
const ZONING_SATELLITE_OUTLINE_COLOR = "#c9d8f0"
const ZONING_LABEL_COLOR = "#1f3454"
export const PLANNING_POLICY_COLORS = Object.freeze({
	tod: "#2E8BC0",
	ppu: "#D96B5F",
	strategic: "#7A6FAF",
	corridor: "#ACC9D9",
})

export function addZoning(
	map: Map,
	{
		baseUrl,
		visibleLayers,
		beforeLayerName,
		isSatellite,
	}: {
		baseUrl: string
		visibleLayers: string[]
		beforeLayerName?: string
		isSatellite: boolean
	}
) {
	const visibility = visibleLayers.includes("zoning") ? "visible" : "none"

	map.addSource(ZONING_SOURCE_NAME, {
		type: "vector",
		tiles: [`${baseUrl}/${ZONING_SOURCE_LAYER}/{z}/{x}/{y}.pbf`],
		promoteId: "zone_code",
	})

	map.addLayer(
		{
			id: "zoning",
			source: ZONING_SOURCE_NAME,
			"source-layer": ZONING_SOURCE_LAYER,
			type: "fill",
			layout: { visibility },
			paint: {
				"fill-color": ZONING_COLOR,
				"fill-opacity": [
					"case",
					["boolean", ["feature-state", "selected"], false],
					0.7,
					["boolean", ["feature-state", "hover"], false],
					0.5,
					0.2,
				],
				"fill-opacity-transition": { duration: 120 },
			},
			metadata: { custom: true },
		},
		beforeLayerName
	)

	map.addLayer(
		{
			id: "zoning-outline",
			source: ZONING_SOURCE_NAME,
			"source-layer": ZONING_SOURCE_LAYER,
			type: "line",
			layout: { visibility },
			paint: {
				"line-color": isSatellite ? ZONING_SATELLITE_OUTLINE_COLOR : ZONING_OUTLINE_COLOR,
				"line-opacity": 0.9,
				"line-width": ["interpolate", ["linear"], ["zoom"], 8, 0.5, 14, 1, 18, 2, 22, 3],
			},
			metadata: { custom: true },
		},
		beforeLayerName
	)

	map.addLayer(
		{
			id: "zoning-labels",
			source: ZONING_SOURCE_NAME,
			"source-layer": ZONING_SOURCE_LAYER,
			type: "symbol",
			minzoom: 14,
			layout: {
				visibility,
				"text-field": ["to-string", ["get", "zone_code"]],
				"text-size": 12,
				"text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
				"text-allow-overlap": false,
			},
			paint: {
				"text-color": ZONING_LABEL_COLOR,
				"text-halo-color": "rgba(255, 255, 255, 0.9)",
				"text-halo-width": 1.5,
			},
			metadata: { custom: true },
		},
		beforeLayerName
	)
}

export function addPlanningPolicy(
	map: Map,
	{
		baseUrl,
		visibleLayers,
		layerName,
		sourceName,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
	}
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/core_public.planning_policy/{z}/{x}/{y}.pbf`],
	})

	const colorExpr: Expression = [
		"match",
		["get", "type"],
		"TOD",
		PLANNING_POLICY_COLORS.tod,
		"PPU",
		PLANNING_POLICY_COLORS.ppu,
		"SPI",
		PLANNING_POLICY_COLORS.strategic,
		"Corridor de transport métropolitain",
		PLANNING_POLICY_COLORS.corridor,
		"Secteur de planification stratégique",
		PLANNING_POLICY_COLORS.strategic,
		"#808080",
	]

	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "core_public.planning_policy",
		type: "fill",
		filter: ["==", ["get", "source"], "PMADR"],
		layout: {
			visibility: visibleLayers.includes("planning-policy") ? "visible" : "none",
		},
		paint: {
			"fill-color": colorExpr,
			"fill-opacity": [
				"match",
				["get", "type"],
				"TOD",
				0.45,
				"PPU",
				0.45,
				"SPI",
				0.4,
				"Corridor de transport métropolitain",
				0.35,
				"Secteur de planification stratégique",
				0.35,
				0,
			],
		},
		metadata: { custom: true },
	})

	// Hatched overlay for PU source
	map.addLayer({
		id: `${layerName}-hatch`,
		source: sourceName,
		"source-layer": "core_public.planning_policy",
		type: "fill",
		filter: ["==", ["get", "source"], "PU"],
		layout: {
			visibility: visibleLayers.includes("planning-policy") ? "visible" : "none",
		},
		paint: {
			"fill-pattern": [
				"match",
				["get", "type"],
				"TOD",
				"planning-hatch-tod",
				"PPU",
				"planning-hatch-ppu",
				"Corridor de transport métropolitain",
				"planning-hatch-corridor",
				"Secteur de planification stratégique",
				"planning-hatch-strategic",
				"SPI",
				"planning-hatch-strategic",
				/* fallback */ "planning-hatch-tod",
			],
			"fill-opacity": 1,
		},
		metadata: { custom: true },
	})

	map.addLayer({
		id: `${layerName}-line`,
		source: sourceName,
		"source-layer": "core_public.planning_policy",
		type: "line",
		filter: ["==", ["get", "source"], "SAD"],
		layout: {
			visibility: visibleLayers.includes("planning-policy") ? "visible" : "none",
		},
		paint: {
			"line-color": colorExpr,
			"line-width": 2,
			"line-opacity": 1,
		},
		metadata: { custom: true },
	})

	map.addLayer({
		id: `${layerName}-hit`,
		source: sourceName,
		"source-layer": "core_public.planning_policy",
		type: "fill",
		layout: {
			visibility: visibleLayers.includes("planning-policy") ? "visible" : "none",
		},
		paint: {
			"fill-color": "transparent",
			"fill-opacity": 0,
		},
		metadata: { custom: true },
	})
}
